import React, { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { ProjectLibrary } from '../types';
import { searchSites, getSiteDrives, SpSite, SpDrive } from '../services/sharepointService';
import { createProjectLibrary, nextLibraryOrder } from '../services/projectLibraryService';
import BoardModal, { BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT } from './BoardModal';

/**
 * Two-step SharePoint library picker. Step 1 searches sites the user can
 * access; step 2 lists that site's document libraries (drives). Selecting a
 * library creates a dagrun_pm_projectlibrary record for the project.
 */
export default function LibraryPickerModal({ pca, projectId, existing, onClose, onAdded }: {
  pca: IPublicClientApplication;
  projectId: string;
  existing: ProjectLibrary[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [query, setQuery] = useState('');
  const [sites, setSites] = useState<SpSite[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [site, setSite] = useState<SpSite | null>(null);
  const [drives, setDrives] = useState<SpDrive[] | null>(null);
  const [drivesLoading, setDrivesLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<number | null>(null);

  // Debounced site search (min 2 chars).
  useEffect(() => {
    const q = query.trim();
    if (debounce.current) window.clearTimeout(debounce.current);
    if (q.length < 2) { setSites([]); setSearching(false); return; }
    setSearching(true); setSearchError(null);
    debounce.current = window.setTimeout(async () => {
      try {
        setSites(await searchSites(pca, q));
      } catch (e) {
        setSearchError(e instanceof Error ? e.message : 'Site search failed');
        setSites([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounce.current) window.clearTimeout(debounce.current); };
  }, [query, pca]);

  const pickSite = async (s: SpSite) => {
    setSite(s); setDrives(null); setDrivesLoading(true); setError(null);
    try {
      setDrives(await getSiteDrives(pca, s.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load libraries for this site');
      setDrives([]);
    } finally {
      setDrivesLoading(false);
    }
  };

  const alreadyLinked = (driveId: string) => existing.some(l => l.dagrun_pm_driveid === driveId);

  const addLibrary = async (drive: SpDrive) => {
    if (!site || saving) return;
    setSaving(true); setError(null);
    try {
      await createProjectLibrary(pca, {
        projectId,
        name: drive.name || site.displayName || site.name || 'Library',
        siteId: site.id,
        driveId: drive.id,
        siteUrl: site.webUrl,
        displayOrder: nextLibraryOrder(existing),
      });
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not link library');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BoardModal open title="Add Document Library" onClose={onClose} width={560}
      footer={<BMCancelButton onClick={onClose} disabled={saving}>Close</BMCancelButton>}>
      {!site ? (
        <>
          <label style={BM_LABEL}>Search SharePoint sites</label>
          <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Type a site name…" style={BM_INPUT} />
          <div style={{ marginTop: 12, maxHeight: 320, overflowY: 'auto' }}>
            {searching && <div style={{ padding: 12, color: '#9198A8', fontSize: 13 }}>Searching…</div>}
            {searchError && <div style={{ padding: 12, color: '#DC2626', fontSize: 13 }}>{searchError}</div>}
            {!searching && query.trim().length >= 2 && sites.length === 0 && !searchError && (
              <div style={{ padding: 12, color: '#9198A8', fontSize: 13 }}>No sites found.</div>
            )}
            {sites.map(s => (
              <button key={s.id} type="button" onClick={() => pickSite(s)}
                style={rowBtn}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <div style={{ fontSize: 14, color: '#0F1117', fontWeight: 500 }}>{s.displayName || s.name || s.webUrl}</div>
                <div style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{s.webUrl}</div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button type="button" onClick={() => { setSite(null); setDrives(null); }}
            style={{ background: 'transparent', border: 'none', color: '#0E7490', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 10 }}>
            ← Back to sites
          </button>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1117', marginBottom: 2 }}>{site.displayName || site.name}</div>
          <div style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>{site.webUrl}</div>
          <label style={BM_LABEL}>Choose a document library</label>
          <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
            {drivesLoading && <div style={{ padding: 12, color: '#9198A8', fontSize: 13 }}>Loading libraries…</div>}
            {drives && drives.length === 0 && !drivesLoading && (
              <div style={{ padding: 12, color: '#9198A8', fontSize: 13 }}>No document libraries on this site.</div>
            )}
            {drives?.map(d => {
              const linked = alreadyLinked(d.id);
              return (
                <div key={d.id} style={{ ...rowBtn, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'default' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#0F1117', fontWeight: 500 }}>{d.name}</div>
                    {d.driveType && <div style={{ fontSize: 11, color: '#9198A8' }}>{d.driveType}</div>}
                  </div>
                  {linked ? (
                    <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>✓ Linked</span>
                  ) : (
                    <BMPrimaryButton onClick={() => addLibrary(d)} disabled={saving}>
                      {saving ? 'Adding…' : 'Add'}
                    </BMPrimaryButton>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

const rowBtn: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '10px 12px', border: 'none', background: 'transparent',
  borderRadius: 6, cursor: 'pointer', borderBottom: '0.5px solid #EAECF0',
};
